import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import logoImg from "@/assets/logo.png";
import {
  Menu,
  X,
  LogOut,
  ArrowRight,
  Activity,
} from "lucide-react";
import { useState } from "react";

export function PublicNavbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#e6e6e6] bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        {/* Brand / Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <img
            src={logoImg}
            alt="EKSUTH Logo"
            className="h-8 w-8 rounded-full border border-[#e6e6e6] object-contain shadow-xs"
          />
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold tracking-tight text-[#000000]">
              EKSUTH Antibiogram
            </span>
            <span className="hidden rounded-full bg-[#f6f5f4] border border-[#e6e6e6] px-2 py-0.5 text-[11px] font-medium text-[#615d59] sm:inline-block">
              Hospital Surveillance
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive
                ? "text-[14px] font-semibold text-[#0075de]"
                : "text-[14px] font-medium text-[#31302e] hover:text-[#000000]"
            }
          >
            Home
          </NavLink>
          <NavLink
            to="/docs"
            className={({ isActive }) =>
              isActive
                ? "text-[14px] font-semibold text-[#0075de]"
                : "text-[14px] font-medium text-[#31302e] hover:text-[#000000]"
            }
          >
            Documentation
          </NavLink>
        </nav>

        {/* Right CTA / Auth Status */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-2.5">
              <Link
                to="/workbench"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#e6e6e6] bg-white px-3 py-1.5 text-xs font-medium text-[#31302e] hover:bg-[#f6f5f4] transition-colors shadow-xs"
              >
                <Activity className="h-3.5 w-3.5 text-[#0075de]" />
                <span>Workbench</span>
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="p-1.5 text-[#615d59] hover:text-[#000000] rounded-md hover:bg-[#f6f5f4] transition-colors"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#e6e6e6] bg-white px-3.5 py-1.5 text-[13px] font-medium text-[#000000] hover:bg-[#f6f5f4] transition-colors shadow-xs"
              >
                <span>Sign In</span>
                <ArrowRight className="h-3.5 w-3.5 text-[#615d59]" />
              </Link>
            </div>
          )}
        </div>

        {/* Mobile menu trigger */}
        <div className="flex md:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-md p-1.5 text-[#31302e] hover:bg-[#f6f5f4] transition-colors"
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="border-b border-[#e6e6e6] bg-white px-4 py-4 md:hidden shadow-soft">
          <div className="flex flex-col space-y-3">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm font-medium text-[#31302e] py-1 hover:text-[#0075de]"
            >
              Home
            </Link>
            <Link
              to="/docs"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm font-medium text-[#31302e] py-1 hover:text-[#0075de]"
            >
              Documentation
            </Link>
            <div className="pt-2 border-t border-[#e6e6e6]">
              {user ? (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/workbench"
                    onClick={() => setMobileMenuOpen(false)}
                    className="inline-flex justify-center items-center gap-2 rounded-md bg-[#0075de] text-white py-2 text-sm font-medium"
                  >
                    Open Workbench
                  </Link>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSignOut();
                    }}
                    className="text-left text-xs text-[#615d59] py-1 hover:text-red-600"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex justify-center items-center rounded-md bg-[#0075de] text-white py-2 text-sm font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
